import { ReactNode, useEffect } from 'react';
import { IconX } from '@tabler/icons-react';

/**
 * Modal — matches DreamsPOS template's modal pattern. Backdrop click and
 * Escape both close. Sizes mirror Bootstrap conventions.
 */
type Size = 'sm' | 'md' | 'lg' | 'xl';

interface Props {
  open: boolean;
  onClose: () => void;
  title?: string;
  size?: Size;
  children: ReactNode;
  footer?: ReactNode;
  hideClose?: boolean;
}

const SIZE: Record<Size, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export function Modal({ open, onClose, title, size = 'md', children, footer, hideClose }: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4"
      onClick={onClose}
    >
      <div
        className={['w-full bg-surface rounded-xl shadow-md animate-slide-up flex flex-col max-h-[90vh]', SIZE[size]].join(' ')}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || !hideClose) && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
            <h3 className="text-16 font-semibold text-heading">{title}</h3>
            {!hideClose && (
              <button
                type="button"
                onClick={onClose}
                className="w-9 h-9 inline-flex items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-heading transition-colors"
                aria-label="Close"
              >
                <IconX size={18} stroke={2} />
              </button>
            )}
          </div>
        )}
        <div className="p-6 overflow-y-auto flex-1 min-h-0">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border flex-shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export default Modal;
